/**
 * Pure derivations for the landlord-facing Additional Documents checklist
 * and the org Terms & Conditions consent row (TT-65), shared by the
 * individual detail page and the combined report's per-member accordions.
 * Mirrors form-gates `seedReviewDocuments` so the tenant's Review list and
 * the landlord checklist always read the same rows.
 */
import type {
	DocumentItem,
	FormConfigV1,
} from '#app/utils/application-form/config'
import { reviewDocumentItems } from '#app/utils/application-form/form-gates'

export interface RequestedDocumentRef {
	id: string
	name: string
}

const sameDoc = (
	a: { id: string; name: string },
	b: { id: string; name: string },
) =>
	a.id === b.id || a.name.trim().toLowerCase() === b.name.trim().toLowerCase()

/**
 * The checklist the landlord sees: the template's configured items (null
 * when the documents section is off) with any landlord-requested documents
 * (requestDocuments intent) folded in as REQUIRED rows — a same-named
 * optional template row is upgraded to mandatory rather than duplicated,
 * and a request still lists when the template section is off.
 */
export function deriveRequiredDocItems(
	config: FormConfigV1,
	requestedDocuments: RequestedDocumentRef[] | null | undefined,
): DocumentItem[] | null {
	const configItems = reviewDocumentItems(config)
	const requested = (requestedDocuments ?? []).filter((row) => row.name.trim())
	if (requested.length === 0) return configItems
	const matchesRequested = (item: { id: string; name: string }) =>
		requested.some((row) => sameDoc(row, item))
	return [
		...(configItems ?? []).map((item) =>
			matchesRequested(item) ? { ...item, mandatory: true } : item,
		),
		...requested
			.filter((row) => !(configItems ?? []).some((item) => sameDoc(item, row)))
			.map((row) => ({ id: row.id, name: row.name, mandatory: true })),
	]
}

export interface OrgTermsLink {
	fileName: string | null
	blobUrl: string
}

/** The org's own Terms & Conditions PDF when the template's terms section is
 * on and carries an upload; null otherwise (the consent card omits the row). */
export function deriveOrgTerms(config: FormConfigV1): OrgTermsLink | null {
	const terms = config.sections.terms
	return terms.enabled && terms.blobUrl
		? { fileName: terms.fileName ?? null, blobUrl: terms.blobUrl }
		: null
}
